/**
 * <p-konfigurator>: Bedienung im Globo-Look (Farb-Dropdown mit Bildern, Schalter, Farbpaletten-Dialog, (i)-Hinweis,
 * Preiszeile), Bedingungen (Blumenfelder), Zeichenzähler, Prüfung beim Absenden des Produktformulars
 * (window-Capture-Phase vor Theme handleSubmit), versteckte deutsche Exportfelder füllen.
 * Keine Texte im JS: Meldungen kommen aus data-messages (locales), Beschriftungen aus dem Markup.
 * Markup + Styles: blocks/p-konfigurator.liquid, Optionen: snippets/p-konfigurator-swatch.liquid.
 */
import { validateForm, normalizeName, stepStates } from '@theme/p-konfigurator-logic'; // Importmap (versionierte URL, sonst liefert das CDN alte Stände)

class PKonfigurator extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this.nameMax = Number(this.dataset.nameMax) || 0;
    this.flowerCount = Number(this.dataset.flowers) || 0;
    this.flowerPrice = Number(this.dataset.flowerPrice) || 0;
    this.expressPrice = Number(this.dataset.expressPrice) || 0;
    this.messages = JSON.parse(this.dataset.messages || '{}');
    this.nameInput = this.querySelector('[data-input="name"]');
    this.openSelect = null;
    // Fail-safe ohne JS: Name + Hauptfarbe sind in der Liquid-Vorlage nativ `required`. Sobald wir laufen,
    // übernimmt unsere eigene Prüfung (onSubmit) inkl. eigener Meldungen → novalidate, damit der Browser nicht
    // zusätzlich seine eigene (unübersetzte) Blase zeigt bzw. das Formular vor unserem Handler blockiert.
    const form = document.getElementById(this.formId);
    if (form) form.setAttribute('novalidate', '');

    // Fail-safe ohne JS: die Farblisten sind in der Liquid-Vorlage eine normale sichtbare Radioliste (kein
    // `hidden`) - erst hier (bzw. schon vorher im Sync-Script direkt nach dem Element, das Aufblitzen der
    // vollen Liste vermeidet) werden sie zum Dropdown aufgewertet: Klasse fürs CSS, Liste zuklappen, Radios aus
    // der Tab-Reihenfolge (Roving-Tabindex übernimmt onKeydown). Idempotent zum Sync-Script.
    this.classList.add('is-enhanced');
    this.querySelectorAll('[data-select-list]').forEach((list) => {
      list.hidden = true;
      list.querySelectorAll('[data-option]').forEach((option, i) => {
        option.id = `${list.id}-o${i}`;
        const input = option.querySelector('input');
        if (input) {
          input.tabIndex = -1;
          input.setAttribute('aria-hidden', 'true');
        }
      });
    });

    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusout', this.onFocusout);
    // window + Capture: läuft vor Theme deklarativem on:submit (component.js hängt an document, Capture)
    window.addEventListener('submit', this.onSubmit, true);
    document.addEventListener('click', this.onDocumentClick);
    // Task 4: Theme Varianten-Auswahl (Farbe/Größe) in die Schritt-Nummerierung einbeziehen. this.details muss
    // VOR dem ersten updateSteps() stehen (Fix 2, Review 2026-09-25): sonst zählt die erste SR-Nummer nur die
    // eigenen Schritte (this.details noch undefined → Fallback auf sich selbst), Sprung erst beim ersten Klick.
    this.details = this.closest('.product-details');
    this.touchedOptions = new Set();
    if (this.details) {
      this.details.addEventListener('change', this.onVariantChange);
      this.variantObserver = new MutationObserver(() => this.markVariantSteps());
      this.details.querySelectorAll('variant-picker').forEach((picker) =>
        this.variantObserver.observe(picker, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-p-step-done'] })
      );
    }

    this.updateCounter();
    this.syncSelects();
    this.updateTotal();
    this.errors = {};
    this.updateSteps();
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.onSubmit, true);
    document.removeEventListener('click', this.onDocumentClick);
    this.details?.removeEventListener('change', this.onVariantChange);
    this.variantObserver?.disconnect();
  }

  /* ── Theme-Varianten-Schritte (Farbe/Größe) ────────────── */

  onVariantChange = (event) => {
    const fieldset = event.target.closest?.('variant-picker fieldset.variant-option');
    if (!fieldset) return;
    this.touchedOptions.add(fieldset.dataset.fieldsetIndex);
    this.markVariantSteps();
  };

  markVariantSteps() {
    this.details?.querySelectorAll('variant-picker fieldset.variant-option').forEach((fieldset) => {
      const done = this.touchedOptions.has(fieldset.dataset.fieldsetIndex);
      if (done && !fieldset.hasAttribute('data-p-step-done')) fieldset.setAttribute('data-p-step-done', '');
      if (!done && fieldset.hasAttribute('data-p-step-done')) fieldset.removeAttribute('data-p-step-done');
    });
  }

  /* ── Zustand ─────────────────────────────────────────── */

  state() {
    // Felder über data-*-Attribute finden (Property-Namen sind je Sprache verschieden, siehe Liquid-Vorlage)
    const checked = (field) => this.querySelector(`[data-field="${field}"] input[type=radio]:checked`)?.value ?? null;
    const flowers = [];
    for (let i = 1; i <= 2; i++) {
      flowers.push({
        on: Boolean(this.querySelector(`[data-flower-toggle="${i}"]`)?.checked),
        color: checked(`flower${i}Color`),
        position: this.querySelector(`[data-field="flower${i}Position"] [data-input="position"]`)?.value ?? '',
      });
    }
    return {
      name: this.nameInput?.value ?? '',
      color: checked('color'),
      flowers,
      express: Boolean(this.querySelector('[data-express]')?.checked),
    };
  }

  /* ── Ereignisse ──────────────────────────────────────── */

  onChange = (event) => {
    const target = event.target;
    if (target.matches('[data-flower-toggle]')) {
      const body = this.querySelector(`[data-flower-body="${target.dataset.flowerToggle}"]`);
      if (body) {
        body.hidden = !target.checked;
        // versteckte Exportfelder ausgenommen: die schaltet allein onSubmit/fillExportFields frei
        body.querySelectorAll('input:not([data-export-kind])').forEach((input) => (input.disabled = !target.checked));
        if (!target.checked) {
          // Schalter wieder aus: Fehler/aria-invalid der Blumensektion zurücksetzen, sonst tauchen beim
          // erneuten Einschalten alte Meldungen wieder auf, bevor die Felder neu geprüft wurden.
          body.querySelectorAll('[data-error]').forEach((error) => (error.hidden = true));
          body.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
          const n = target.dataset.flowerToggle;
          if (this.errors) {
            delete this.errors[`flower${n}Color`];
            delete this.errors[`flower${n}Position`];
          }
        }
      }
    }
    if (target.type === 'radio') {
      const select = target.closest('[data-select]');
      if (select) {
        this.syncSelect(select);
        this.closeSelect(select, true);
      }
    }
    this.clearError(target);
    this.updateTotal();
    this.updateSteps();
  };

  onInput = (event) => {
    if (event.target === this.nameInput) this.updateCounter();
    this.clearError(event.target);
    this.updateSteps();
  };

  onClick = (event) => {
    const selectBtn = event.target.closest('[data-select-btn]');
    if (selectBtn) {
      const select = selectBtn.closest('[data-select]');
      if (selectBtn.getAttribute('aria-expanded') === 'true') this.closeSelect(select, true);
      else this.openSelectList(select);
      return;
    }
    const open = event.target.closest('[data-palette-open]');
    if (open) {
      this.openDialog(document.getElementById(open.dataset.paletteOpen));
      return;
    }
    const close = event.target.closest('[data-palette-close]');
    if (close) close.closest('dialog')?.close();
    if (event.target instanceof HTMLDialogElement) event.target.close(); // Klick auf den Hintergrund
    const info = event.target.closest('[data-info-btn]');
    if (info) {
      const wrap = info.closest('[data-info]');
      const isOpen = wrap.classList.toggle('is-open');
      info.setAttribute('aria-expanded', String(isOpen));
    }
  };

  onDocumentClick = (event) => {
    if (this.openSelect && !this.openSelect.contains(event.target)) this.closeSelect(this.openSelect, false);
    this.querySelectorAll('[data-info].is-open').forEach((wrap) => {
      if (wrap.contains(event.target)) return;
      wrap.classList.remove('is-open');
      wrap.querySelector('[data-info-btn]')?.setAttribute('aria-expanded', 'false');
    });
  };

  onFocusout = (event) => {
    const select = event.target.closest?.('[data-select]');
    if (!select || select !== this.openSelect) return;
    if (event.relatedTarget && select.contains(event.relatedTarget)) return;
    // Fokus verlässt das Dropdown (Tab) → schließen, Auswahl bleibt
    if (event.relatedTarget) this.closeSelect(select, false);
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      const wrap = event.target.closest?.('[data-info].is-open');
      if (wrap) {
        wrap.classList.remove('is-open');
        wrap.querySelector('[data-info-btn]')?.setAttribute('aria-expanded', 'false');
      }
    }
    const select = event.target.closest?.('[data-select]');
    if (!select) return;
    const btn = select.querySelector('[data-select-btn]');
    const list = select.querySelector('[data-select-list]');
    const isOpen = btn.getAttribute('aria-expanded') === 'true';

    if (event.target === btn && !isOpen) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.openSelectList(select);
      }
      return;
    }
    if (!isOpen) return;

    const options = [...list.querySelectorAll('[data-option]')];
    const current = options.findIndex((o) => o.classList.contains('is-active'));
    const move = (index) => {
      event.preventDefault();
      this.setActive(select, options[Math.max(0, Math.min(options.length - 1, index))]);
    };
    switch (event.key) {
      case 'ArrowDown': return move(current + 1);
      case 'ArrowUp': return move(current - 1);
      case 'Home': return move(0);
      case 'End': return move(options.length - 1);
      case 'PageDown': return move(current + 5);
      case 'PageUp': return move(current - 5);
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        return this.closeSelect(select, true);
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const input = options[current]?.querySelector('input');
        if (input && !input.disabled) {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          } else this.closeSelect(select, true);
        }
        return;
      }
      case 'Tab':
        return this.closeSelect(select, false);
      default: {
        // Tippen springt zur ersten passenden Farbe (Nummer oder Name)
        if (event.key.length !== 1) return;
        const needle = event.key.toLowerCase();
        const hit = options.find((o) => {
          const label = (o.querySelector('input')?.dataset.label || '').toLowerCase();
          return label.startsWith(needle) || label.split(' - ').pop().startsWith(needle);
        });
        if (hit) move(options.indexOf(hit));
      }
    }
  };

  onSubmit = (event) => {
    const form = event.target;
    // getAttribute: form.id liefert bei Shopify-Formularen das <input name="id"> (Variante), nicht die ID
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.formId) return;
    const state = this.state();
    const errors = validateForm(state, { nameMax: this.nameMax, flowers: this.flowerCount });
    if (Object.keys(errors).length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.errors = errors;
      this.updateSteps();
      this.showErrors(errors);
      return;
    }
    this.errors = {};
    if (this.nameInput) this.nameInput.value = normalizeName(this.nameInput.value);
    this.querySelectorAll('[data-input="position"]').forEach((input) => {
      if (!input.disabled) input.value = normalizeName(input.value);
    });
    this.fillExportFields();
  };

  /**
   * Nur Shop-Sprache ≠ Deutsch (sonst gibt es keine [data-export-kind]-Felder): jedes versteckte `_<deutscher
   * Schlüssel>`-Feld verweist über `data-export-for="<id>"` explizit auf sein sichtbares Feld bzw. dessen
   * Radiogruppe/Schalter (DOM-Reihenfolge bleibt unverändert = Reihenfolge in der Bestellung, die Kopplung selbst
   * hängt aber nicht mehr daran) und bekommt dessen deutschen Exportwert. Nur Felder zu tatsächlich gewählten/
   * aktiven Optionen werden gesendet, alle übrigen bleiben disabled (wie die sichtbaren Felder).
   */
  fillExportFields() {
    this.querySelectorAll('[data-export-kind]').forEach((hidden) => {
      const source = document.getElementById(hidden.dataset.exportFor);
      let value = '';
      if (source) {
        switch (hidden.dataset.exportKind) {
          case 'radio': // source = Farbliste → gewählte, aktive Option
            value = source.querySelector('input[type=radio]:checked:not(:disabled)')?.dataset.export ?? '';
            break;
          case 'check': { // source = <label> des Schalters
            const box = source.querySelector('input[type=checkbox]');
            value = box?.checked && !box.disabled ? box.dataset.export ?? '' : '';
            break;
          }
          case 'text': // source = Freitext (bereits normalisiert)
            value = source.disabled ? '' : normalizeName(source.value);
            break;
        }
      }
      hidden.value = value;
      hidden.disabled = !value;
    });
  }

  /* ── Dropdown ────────────────────────────────────────── */

  openSelectList(select) {
    if (this.openSelect && this.openSelect !== select) this.closeSelect(this.openSelect, false);
    const btn = select.querySelector('[data-select-btn]');
    const list = select.querySelector('[data-select-list]');
    list.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    this.openSelect = select;
    const options = [...list.querySelectorAll('[data-option]')];
    const selected = options.find((o) => o.querySelector('input')?.checked);
    this.setActive(select, selected || options[0], selected ? 'center' : 'nearest');
    list.focus({ preventScroll: true });
  }

  closeSelect(select, focusButton) {
    if (!select) return;
    const btn = select.querySelector('[data-select-btn]');
    const list = select.querySelector('[data-select-list]');
    list.hidden = true;
    list.removeAttribute('aria-activedescendant');
    btn.setAttribute('aria-expanded', 'false');
    if (this.openSelect === select) this.openSelect = null;
    if (focusButton) btn.focus({ preventScroll: true });
  }

  setActive(select, option, block = 'nearest') {
    if (!option) return;
    const list = select.querySelector('[data-select-list]');
    list.querySelectorAll('[data-option].is-active').forEach((o) => o.classList.remove('is-active'));
    option.classList.add('is-active');
    list.setAttribute('aria-activedescendant', option.id);
    // Nur innerhalb der Liste scrollen (kein Seiten-Sprung)
    const top = option.offsetTop;
    const bottom = top + option.offsetHeight;
    if (block === 'center') list.scrollTop = top - (list.clientHeight - option.offsetHeight) / 2;
    else if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }

  syncSelects() {
    this.querySelectorAll('[data-select]').forEach((select) => this.syncSelect(select));
  }

  syncSelect(select) {
    const value = select.querySelector('[data-select-value]');
    const field = select.closest('[data-field]');
    const swatch = field?.querySelector('[data-swatch]');
    const selected = field?.querySelector('[data-selected]');
    let chosen = null;
    select.querySelectorAll('[data-option]').forEach((option) => {
      const isChecked = Boolean(option.querySelector('input')?.checked);
      option.setAttribute('aria-selected', String(isChecked));
      if (isChecked) chosen = option;
    });
    if (!chosen) {
      swatch?.replaceChildren();
      if (selected) selected.hidden = true;
      return;
    }
    const label = chosen.querySelector('input').dataset.label;
    value.replaceChildren(Object.assign(document.createElement('span'), { textContent: label }));
    const img = chosen.querySelector('.p-konf__option-img img');
    if (swatch) swatch.replaceChildren(...(img ? [Object.assign(img.cloneNode(true), { loading: 'eager', width: 56, height: 56 })] : []));
    if (selected) {
      selected.textContent = (this.messages.selected || '[value]').replace('[value]', label);
      selected.hidden = false;
    }
  }

  /* ── Dialog, Zähler, Preiszeile, Fehler ─────────────── */

  openDialog(dialog) {
    if (!dialog) return;
    // Top-Layer (showModal) → nicht von Section-Wrappern mit transform/contain eingefangen
    dialog.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    dialog.showModal();
  }

  updateCounter() {
    const counter = this.querySelector('[data-counter]');
    if (!counter || !this.nameInput) return;
    const count = [...this.nameInput.value].length;
    counter.textContent = (this.messages.counter || '').replace('[count]', String(count)).replace('[max]', String(this.nameMax));
  }

  updateTotal() {
    const total = this.querySelector('[data-total]');
    if (!total) return;
    const state = this.state();
    const flowers = state.flowers.slice(0, this.flowerCount).filter((f) => f.on).length;
    const cents = flowers * this.flowerPrice + (state.express ? this.expressPrice : 0);
    if (cents <= 0) {
      total.hidden = true;
      total.replaceChildren();
      return;
    }
    const lang = document.documentElement.lang || undefined;
    const currency = window.Shopify?.currency?.active || 'EUR';
    let amount;
    try {
      amount = new Intl.NumberFormat(lang, { style: 'currency', currency }).format(cents / 100);
    } catch {
      amount = (cents / 100).toFixed(2);
    }
    const [before, after = ''] = (this.messages.surcharge || '[amount]').split('[amount]');
    const strong = Object.assign(document.createElement('span'), { className: 'p-konf__total-amount', textContent: amount });
    total.replaceChildren(before, strong, after);
    total.hidden = false;
  }

  updateSteps() {
    const states = stepStates(this.state(), { nameMax: this.nameMax, flowers: this.flowerCount }, this.errors || {});
    // Fix 2 (Review 2026-09-25): sichtbare Nummer = Position unter ALLEN Schritten im .product-details
    // (Theme Varianten-Optionen + unsere eigenen), nicht nur unter unseren eigenen.
    const scope = this.details || this;
    const order = [...scope.querySelectorAll('fieldset.variant-option, .p-konf__step')];
    this.querySelectorAll('[data-step]').forEach((step) => {
      const state = states[step.dataset.step] || 'open';
      step.dataset.state = state;
      const number = order.indexOf(step) + 1;
      const numSr = step.querySelector('[data-step-sr-num]');
      const doneSr = step.querySelector('[data-step-sr-done]');
      if (numSr) numSr.textContent = number > 0 ? (this.messages.step || '').replace('[number]', String(number)) : '';
      if (doneSr) doneSr.textContent = state === 'done' ? this.messages.step_done || '' : '';
    });
  }

  clearError(input) {
    const field = input.closest('[data-field]');
    if (!field) return;
    const error = field.querySelector('[data-error]');
    if (error) error.hidden = true;
    field.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    const key = field.dataset.field;
    if (this.errors && key in this.errors) delete this.errors[key];
  }

  showErrors(errors) {
    let first = null;
    for (const [field, code] of Object.entries(errors)) {
      const wrapper = this.querySelector(`[data-field="${field}"]`);
      const error = wrapper?.querySelector('[data-error]');
      if (!error) continue;
      const key = field === 'color' || field.endsWith('Color') ? 'color' : code;
      // [max] im Text füllen (Liquid-t füllt nur {{ max }}-Platzhalter, nicht [max])
      error.textContent = (this.messages[key] || this.messages.required || '').replace('[max]', String(this.nameMax));
      error.hidden = false;
      const control = wrapper.querySelector('[data-select-btn], input:not([type=radio])');
      control?.setAttribute('aria-invalid', 'true');
      if (!first) first = control;
    }
    if (first) {
      first.focus({ preventScroll: true });
      const head = first.closest('[data-step]')?.querySelector('.p-konf__step-head') || first;
      const offset = (parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0) + 16;
      window.scrollTo({ top: head.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('p-konfigurator')) customElements.define('p-konfigurator', PKonfigurator);
